import React from "react";

const GROUP_COLORS = {
    "SPM":   "#0F6A71",
    "DILA":  "#1A5BA8",
    "INSP":  "#A85E15",
    "SGDSN": "#3D5A7C",
};
const FALLBACK_COLOR = "#1E6B5E";

const PAD_LEFT = 40;
const PAD_RIGHT = 40;
const PAD_TOP = 12;
const PAD_BOTTOM = 32;
const W = 500;
const H = 180;
const CHART_W = W - PAD_LEFT - PAD_RIGHT;
const CHART_H = H - PAD_TOP - PAD_BOTTOM;

function getLast7Days() {
    const days = [];
    for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        days.push(d.toISOString().slice(0, 10));
    }
    return days;
}

function xOf(i) {
    return PAD_LEFT + (i / 6) * CHART_W;
}

function yOf(v) {
    return PAD_TOP + ((5 - v) / 4) * CHART_H;
}

function formatDate(dateStr) {
    return new Date(dateStr + "T12:00:00").toLocaleDateString("fr-FR", {
        weekday: "short", day: "numeric", month: "short",
    });
}

export default function MoodTrendChart({historyByGroup, groups}) {
    if (!historyByGroup || Object.keys(historyByGroup).length === 0) return null;

    const dates = getLast7Days();
    const groupIds = Object.keys(historyByGroup);

    const groupLookup = {};
    for (const [groupId, rows] of Object.entries(historyByGroup)) {
        groupLookup[groupId] = {};
        for (const row of rows) {
            groupLookup[groupId][row.mood_date] = {avg: row.avg_mood, votes: row.votes};
        }
    }

    return (
        <div>
            <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{display: "block"}}>
                {/* Grille Y + labels */}
                {[1, 2, 3, 4, 5].map(level => (
                    <g key={level}>
                        <line
                            x1={PAD_LEFT} y1={yOf(level)}
                            x2={W - PAD_RIGHT} y2={yOf(level)}
                            stroke="#E5E7EB" strokeWidth="1"
                        />
                        <text
                            x={PAD_LEFT - 6} y={yOf(level) + 4}
                            fontSize="10" fill="#9CA3AF" textAnchor="end"
                        >{level}</text>
                    </g>
                ))}

                {/* Labels X */}
                {dates.map((date, i) => (
                    <text
                        key={date}
                        x={xOf(i)} y={H - 4}
                        fontSize="9" fill="#9CA3AF" textAnchor="middle"
                    >
                        {new Date(date + "T12:00:00").toLocaleDateString("fr-FR", {weekday: "short", day: "numeric"})}
                    </text>
                ))}

                {/* Courbes par groupe */}
                {groupIds.map(groupId => {
                    const group = groups.find(g => String(g.id) === String(groupId));
                    const name = group?.name || groupId;
                    const color = GROUP_COLORS[name] || FALLBACK_COLOR;
                    const lookup = groupLookup[groupId];

                    const segments = [];
                    let current = [];
                    for (let i = 0; i < dates.length; i++) {
                        const entry = lookup[dates[i]];
                        if (entry) {
                            current.push({i, ...entry});
                        } else {
                            if (current.length > 0) {segments.push(current); current = [];}
                        }
                    }
                    if (current.length > 0) segments.push(current);

                    const allPoints = segments.flat();
                    const lastPoint = allPoints[allPoints.length - 1];

                    return (
                        <g key={groupId}>
                            {segments.map((seg, si) => (
                                seg.length > 1 && (
                                    <polyline
                                        key={si}
                                        points={seg.map(p => `${xOf(p.i)},${yOf(p.avg)}`).join(" ")}
                                        fill="none"
                                        stroke={color}
                                        strokeWidth="2"
                                        strokeLinejoin="round"
                                        strokeLinecap="round"
                                    />
                                )
                            ))}
                            {allPoints.map(p => (
                                <circle
                                    key={`${groupId}-${dates[p.i]}`}
                                    cx={xOf(p.i)} cy={yOf(p.avg)}
                                    r="4" fill={color} stroke="#fff" strokeWidth="1.5"
                                >
                                    <title>{`${name} — ${formatDate(dates[p.i])}\nmoy. ${p.avg}/5 · ${p.votes} vote${p.votes > 1 ? "s" : ""}`}</title>
                                </circle>
                            ))}
                            {/* Label du groupe au dernier point */}
                            {lastPoint && (
                                <text
                                    x={xOf(lastPoint.i) + 6}
                                    y={yOf(lastPoint.avg) + 4}
                                    fontSize="9" fill={color} fontWeight="bold"
                                >{name}</text>
                            )}
                        </g>
                    );
                })}
            </svg>

            {/* Légende — tous les groupes, grisés si aucun vote */}
            <div style={{display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8}}>
                {groups.map(group => {
                    const name = group.name;
                    const color = GROUP_COLORS[name] || FALLBACK_COLOR;
                    const hasData = historyByGroup[String(group.id)] !== undefined;
                    return (
                        <div key={group.id} style={{display: "flex", alignItems: "center", gap: 5, opacity: hasData ? 1 : 0.4}}>
                            <span style={{
                                display: "inline-block", width: 10, height: 10,
                                borderRadius: "50%", backgroundColor: hasData ? color : "#9CA3AF", flexShrink: 0,
                            }}/>
                            <span style={{fontSize: 12, color: hasData ? "#4B5563" : "#9CA3AF"}}>
                                {name}{!hasData ? " (aucun vote)" : ""}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
